/**
 * IhsSlot — match-detail walker.
 *
 * Receives the engine-shipped `widgets[]` array from
 * `useMatchDetail()` and renders each entry IN ORDER. The engine
 * controls layout via the array order; the render side never
 * re-orders, never injects, never drops a known template.
 *
 * Each widget envelope is `{template, data}` typed only loosely on the
 * shared-types side (`Record<string, unknown>` data). The IHS bundle
 * narrows `data` inside each widget after the registry has matched
 * `template`. Unknown templates fall through to `RegistryRenderer`,
 * which warns once and returns null — the layout collapses cleanly.
 *
 * Each rendered widget passes through `<RegistryRenderer>`, which wraps
 * it in `<ComponentGate id={template}>`. Disabling a single IHS
 * template in `runtimeConfig.components` (e.g.
 * `runtimeConfig.components['ihs-venue-referee'] = false`) skips that
 * widget without touching siblings. The component-flag id matches the
 * template id for the IHS bundle.
 */

import type { ReactElement } from 'react';
import type { MatchDetailWidget } from '@aamp/shared-types';
import { ErrorBoundary } from '../particles/ErrorBoundary.js';
import { AnimatedWidget } from '../particles/AnimatedWidget.js';
import { RegistryRenderer } from '../system/RegistryRenderer.js';
import {
  buildWidgetRegistry,
  type WidgetRegistry,
} from '../system/widgetRegistry.js';
import { reportError } from '../../observability.js';
import { IHS_SCHEMAS, type IhsTemplateId } from '../../types/ihsSchemas.js';
import { IhsOverview } from '../widgets/ihs/IhsOverview.js';
import { IhsPreviousMeetings } from '../widgets/ihs/IhsPreviousMeetings.js';
import { IhsVictoryProbability } from '../widgets/ihs/IhsVictoryProbability.js';
import { IhsLast5Games } from '../widgets/ihs/IhsLast5Games.js';
import { IhsTopScorers } from '../widgets/ihs/IhsTopScorers.js';
import { IhsSeasonStats } from '../widgets/ihs/IhsSeasonStats.js';
import { IhsGoals } from '../widgets/ihs/IhsGoals.js';
import { IhsOverGoals } from '../widgets/ihs/IhsOverGoals.js';
import { IhsShots } from '../widgets/ihs/IhsShots.js';
import { IhsAvgMatchStats } from '../widgets/ihs/IhsAvgMatchStats.js';
import { IhsVenueReferee } from '../widgets/ihs/IhsVenueReferee.js';
import { IhsPopularOdds } from '../widgets/ihs/IhsPopularOdds.js';
import type {
  IhsAvgMatchStatsData,
  IhsGoalsData,
  IhsLastFiveGamesData,
  IhsOverGoalsData,
  IhsOverviewData,
  IhsPopularOddsData,
  IhsPreviousMeetingsData,
  IhsSeasonStatsData,
  IhsShotsData,
  IhsTopScorersData,
  IhsVenueRefereeData,
  IhsVictoryProbabilityData,
} from '../../types/ihs.js';

// ---------------------------------------------------------------------------
// Adapters — narrow the registry's erased `unknown` data into the widget's
// concrete IHS payload type. Engine `shapeVersion` is the trust boundary;
// these wrappers are 1-line type assertions.
// ---------------------------------------------------------------------------

function IhsOverviewAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsOverview data={data as IhsOverviewData} />;
}
function IhsPreviousMeetingsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsPreviousMeetings data={data as IhsPreviousMeetingsData} />;
}
function IhsVictoryProbabilityAdapter({
  data,
}: {
  data: unknown;
}): ReactElement {
  return <IhsVictoryProbability data={data as IhsVictoryProbabilityData} />;
}
function IhsLast5GamesAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsLast5Games data={data as IhsLastFiveGamesData} />;
}
function IhsTopScorersAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsTopScorers data={data as IhsTopScorersData} />;
}
function IhsSeasonStatsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsSeasonStats data={data as IhsSeasonStatsData} />;
}
function IhsGoalsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsGoals data={data as IhsGoalsData} />;
}
function IhsOverGoalsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsOverGoals data={data as IhsOverGoalsData} />;
}
function IhsShotsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsShots data={data as IhsShotsData} />;
}
function IhsAvgMatchStatsAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsAvgMatchStats data={data as IhsAvgMatchStatsData} />;
}
function IhsVenueRefereeAdapter({ data }: { data: unknown }): ReactElement {
  return <IhsVenueReferee data={data as IhsVenueRefereeData} />;
}
function IhsPopularOddsAdapter({
  data,
}: {
  data: unknown;
}): ReactElement | null {
  return <IhsPopularOdds data={data as IhsPopularOddsData} />;
}

// ---------------------------------------------------------------------------
// Match-detail registry — every IHS template registers under its own
// component flag id (matches the runtime-config flag).
// ---------------------------------------------------------------------------

const MATCH_DETAIL_REGISTRY: WidgetRegistry = buildWidgetRegistry([
  ['ihs-overview', { id: 'ihs-overview', component: IhsOverviewAdapter }],
  [
    'ihs-previous-meetings',
    { id: 'ihs-previous-meetings', component: IhsPreviousMeetingsAdapter },
  ],
  [
    'ihs-victory-probability',
    {
      id: 'ihs-victory-probability',
      component: IhsVictoryProbabilityAdapter,
    },
  ],
  ['ihs-last-5-games', { id: 'ihs-last-5-games', component: IhsLast5GamesAdapter }],
  ['ihs-top-scorers', { id: 'ihs-top-scorers', component: IhsTopScorersAdapter }],
  ['ihs-season-stats', { id: 'ihs-season-stats', component: IhsSeasonStatsAdapter }],
  ['ihs-goals', { id: 'ihs-goals', component: IhsGoalsAdapter }],
  ['ihs-over-goals', { id: 'ihs-over-goals', component: IhsOverGoalsAdapter }],
  ['ihs-shots', { id: 'ihs-shots', component: IhsShotsAdapter }],
  [
    'ihs-avg-match-stats',
    { id: 'ihs-avg-match-stats', component: IhsAvgMatchStatsAdapter },
  ],
  [
    'ihs-venue-referee',
    { id: 'ihs-venue-referee', component: IhsVenueRefereeAdapter },
  ],
  [
    'ihs-popular-odds',
    { id: 'ihs-popular-odds', component: IhsPopularOddsAdapter },
  ],
]);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface IhsSlotProps {
  widgets: MatchDetailWidget[];
}

/**
 * Validate one envelope's `data` payload against its template schema.
 * Returns `true` when the envelope passes, or when the template is not
 * one of the IHS bundle (the registry handles unknowns separately).
 * Returns `false` and reports via `reportError` when zod parsing fails.
 *
 * Errors NEVER throw — the caller skips the widget instead of crashing
 * the entire match-detail page.
 */
function validateIhsEnvelope(env: MatchDetailWidget, index: number): boolean {
  const schema = IHS_SCHEMAS[env.template as IhsTemplateId];
  if (!schema) return true; // unknown template — let the registry warn instead.
  const result = schema.safeParse(env.data);
  if (result.success) return true;
  // Surface the drift via observability. Production is a no-op until
  // P11 wires Sentry; in dev the console gets a structured log line.
  const issues = result.error.issues.map((issue) => ({
    path: issue.path.join('.') || '(root)',
    message: issue.message,
  }));
  reportError(
    new Error(`IHS envelope failed schema validation: ${env.template}`),
    {
      scope: 'IhsSlot',
      template: env.template,
      index,
      issues,
    },
  );
  return false;
}

/**
 * Render the engine's `widgets[]` array IN ORDER.
 *
 * Each widget is wrapped in `<ErrorBoundary>` so a malformed envelope
 * doesn't tear down siblings, and in `<AnimatedWidget>` for the staged
 * fade-in cascade. The first widget (typically `ihs-overview` or
 * `ihs-popular-odds`) gets no entrance delay so it's the LCP element.
 *
 * Each envelope is safe-parsed via the matching schema in
 * `ihsSchemas.ts` BEFORE rendering. A schema failure logs to
 * `reportError` and the widget is skipped — the rest of the bundle
 * still renders, sibling widgets are unaffected.
 */
export function IhsSlot({ widgets }: IhsSlotProps): ReactElement {
  return (
    <>
      {widgets.map((env, index) => {
        // Cascade the entrance animation in 60ms steps capped at 300ms.
        const delay = index === 0 ? 0 : Math.min(index * 60, 300);
        if (!validateIhsEnvelope(env, index)) return null;
        return (
          <ErrorBoundary key={`${env.template}:${index}`}>
            <AnimatedWidget delay={delay}>
              <RegistryRenderer
                registry={MATCH_DETAIL_REGISTRY}
                template={env.template}
                data={env.data}
              />
            </AnimatedWidget>
          </ErrorBoundary>
        );
      })}
    </>
  );
}

export default IhsSlot;
